import { MODE_CREDIT_TOPUP, MODE_VOICE_TOPUP } from "../_shared/payment-modes.ts";

export const PSE_PAYMENT_METHOD_ID = "pse";
export const PSE_MODES: ReadonlySet<string> = new Set([MODE_CREDIT_TOPUP, MODE_VOICE_TOPUP]);
export const PSE_ENTITY_TYPES = ["individual", "association"] as const;
export const PSE_ID_TYPES = ["CC", "CE", "NIT", "TI", "PAS", "RC", "TE", "DI", "Otro"] as const;
export const PSE_RETURN_PARAM = "pse_return";

export type PseEntityType = typeof PSE_ENTITY_TYPES[number];
export type PseIdType = typeof PSE_ID_TYPES[number];
export type PseReturnKind = "topup";

export interface PseAddress {
  zip_code: string;
  street_name: string;
  street_number: string;
  neighborhood: string;
  city: string;
  federal_unit: string;
}

export interface PsePayer {
  financial_institution: string;
  entity_type: PseEntityType;
  identification: { type: PseIdType; number: string };
  first_name: string;
  last_name: string;
  phone: string;
  address: PseAddress;
}

export type PseParseResult = { ok: true; payer: PsePayer } | { ok: false; error: string };

const DEFAULT_CALLBACK_ORIGIN = "https://flow-chat.desarrollandoando.com";
const CALLBACK_HOSTS = new Set([
  "flow-chat.desarrollandoando.com",
  "agentevirtual.etb.com.co",
  "flow-and-etb.vercel.app",
]);
const MAX_CALLBACK_LENGTH = 512;

function text(value: unknown, max: number): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim().replace(/\s+/g, " ");
  if (!trimmed || trimmed.length > max) return null;
  return trimmed;
}

export function pseIdTypeFromBilling(type: string | null | undefined): PseIdType {
  if (type === "PP") return "PAS";
  return (PSE_ID_TYPES as readonly string[]).includes(String(type)) ? type as PseIdType : "CC";
}

export function parsePsePayer(input: unknown): PseParseResult {
  if (!input || typeof input !== "object") return { ok: false, error: "Faltan los datos del pago por PSE." };
  const raw = input as Record<string, unknown>;

  const bank = text(raw.financial_institution, 20);
  if (!bank || !/^[A-Za-z0-9_-]+$/.test(bank)) return { ok: false, error: "Elige tu banco." };

  const entityType = raw.entity_type;
  if (!(PSE_ENTITY_TYPES as readonly unknown[]).includes(entityType)) {
    return { ok: false, error: "Elige si pagas como persona natural o jurídica." };
  }

  const identification = (raw.identification ?? {}) as Record<string, unknown>;
  const idType = identification.type;
  if (!(PSE_ID_TYPES as readonly unknown[]).includes(idType)) return { ok: false, error: "Elige el tipo de documento." };
  const idNumber = text(identification.number, 20);
  if (!idNumber || !/^[A-Za-z0-9-]{4,20}$/.test(idNumber)) return { ok: false, error: "Escribe un número de documento válido." };

  const firstName = text(raw.first_name, 80);
  if (!firstName) return { ok: false, error: "Escribe el nombre del titular." };
  const lastName = text(raw.last_name, 80);
  if (!lastName) return { ok: false, error: "Escribe el apellido del titular." };

  const phone = typeof raw.phone === "string" ? raw.phone.replace(/\D/g, "") : "";
  if (!/^\d{7,10}$/.test(phone)) return { ok: false, error: "Escribe un teléfono de 7 a 10 dígitos." };

  const address = (raw.address ?? {}) as Record<string, unknown>;
  const zip = typeof address.zip_code === "string" ? address.zip_code.trim() : "";
  if (!/^\d{6}$/.test(zip)) return { ok: false, error: "Escribe un código postal de 6 dígitos." };
  const streetName = text(address.street_name, 100);
  if (!streetName) return { ok: false, error: "Escribe la dirección." };
  const streetNumber = text(address.street_number, 20);
  if (!streetNumber) return { ok: false, error: "Escribe el número de la dirección." };
  const neighborhood = text(address.neighborhood, 80);
  if (!neighborhood) return { ok: false, error: "Escribe el barrio." };
  const city = text(address.city, 80);
  if (!city) return { ok: false, error: "Escribe la ciudad." };
  const federalUnit = text(address.federal_unit, 80);
  if (!federalUnit) return { ok: false, error: "Escribe el departamento." };

  return {
    ok: true,
    payer: {
      financial_institution: bank,
      entity_type: entityType as PseEntityType,
      identification: { type: idType as PseIdType, number: idNumber },
      first_name: firstName,
      last_name: lastName,
      phone,
      address: {
        zip_code: zip,
        street_name: streetName,
        street_number: streetNumber,
        neighborhood,
        city,
        federal_unit: federalUnit,
      },
    },
  };
}

function callbackOrigin(origin: string | null): string {
  if (!origin) return DEFAULT_CALLBACK_ORIGIN;
  let url: URL;
  try {
    url = new URL(origin);
  } catch {
    return DEFAULT_CALLBACK_ORIGIN;
  }
  if (url.protocol !== "https:" || !CALLBACK_HOSTS.has(url.hostname)) return DEFAULT_CALLBACK_ORIGIN;
  return url.origin;
}

export function pseCallbackUrl(origin: string | null): string {
  const path = `/app/settings/account?${PSE_RETURN_PARAM}=topup`;
  const url = `${callbackOrigin(origin)}${path}`;
  return url.length <= MAX_CALLBACK_LENGTH ? url : `${DEFAULT_CALLBACK_ORIGIN}${path}`;
}

export function psePaymentFields(input: {
  payer: PsePayer;
  email: string;
  clientIp: string;
  callbackUrl: string;
}): Record<string, unknown> {
  const { payer } = input;
  return {
    payment_method_id: PSE_PAYMENT_METHOD_ID,
    callback_url: input.callbackUrl,
    additional_info: { ip_address: input.clientIp },
    transaction_details: { financial_institution: payer.financial_institution },
    payer: {
      email: input.email,
      entity_type: payer.entity_type,
      first_name: payer.first_name,
      last_name: payer.last_name,
      identification: { type: payer.identification.type, number: payer.identification.number },
      phone: { area_code: "57", number: payer.phone },
      address: { ...payer.address },
    },
  };
}

export function pseRedirectUrl(mpPayment: Record<string, unknown>): string | null {
  const details = mpPayment.transaction_details as Record<string, unknown> | undefined;
  const url = details?.external_resource_url;
  if (typeof url !== "string") return null;
  try {
    return new URL(url).protocol === "https:" ? url : null;
  } catch {
    return null;
  }
}

function isIpv4(ip: string): boolean {
  const parts = ip.split(".");
  return parts.length === 4 && parts.every((part) => /^\d{1,3}$/.test(part) && Number(part) <= 255);
}

function isIpv6(ip: string): boolean {
  if (!ip.includes(":") || !/^[0-9a-fA-F:.]{2,45}$/.test(ip)) return false;
  try {
    return new URL(`http://[${ip}]/`).hostname.length > 2;
  } catch {
    return false;
  }
}

export function isUsableClientIp(ip: string): boolean {
  return isIpv4(ip) || isIpv6(ip);
}
